import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  let location;
  try {
    location = useLocation();
  } catch {
    location = { pathname: typeof window !== 'undefined' ? window.location.pathname : '/' };
  }

  const isKids = Boolean(
    location?.pathname === '/kids' ||
    location?.pathname?.startsWith('/kids/') ||
    location?.pathname?.startsWith('/kids?') ||
    location?.pathname === '/app/kids' ||
    location?.pathname?.startsWith('/app/kids/') ||
    location?.pathname?.startsWith('/app/kids?') ||
    location?.pathname?.includes('/kids') ||
    (typeof window !== 'undefined' && sessionStorage.getItem('last_dashboard_portal') === 'kids')
  );

  // Main App Theme (Defaults to 'system', persisted under 'theme')
  const [mainTheme, setMainThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'system';
    }
    return 'system';
  });

  // Kids Portal Theme (Defaults to 'system' or inherits 'theme', persisted under 'kids_theme')
  const [kidsTheme, setKidsThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('kids_theme') || localStorage.getItem('theme') || 'system';
    }
    return 'system';
  });

  const activeTheme = isKids ? kidsTheme : mainTheme;

  // Real-time system theme listener
  const [systemIsDark, setSystemIsDark] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Resolve 'system' or string to 'dark'/'light'
  const resolvedTheme = useMemo(() => {
    if (activeTheme === 'system') {
      return systemIsDark ? 'dark' : 'light';
    }
    return activeTheme || 'light';
  }, [activeTheme, systemIsDark]);

  // Apply theme class to documentElement
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = window.document.documentElement;

    root.classList.add('theme-switching');
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    requestAnimationFrame(() => {
      root.classList.remove('theme-switching');
    });
  }, [resolvedTheme]);

  const setMainTheme = useCallback((newTheme) => {
    setMainThemeState((prev) => {
      const resolved = typeof newTheme === 'function' ? newTheme(prev) : newTheme;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', resolved);
      }
      return resolved;
    });
  }, []);

  const setKidsTheme = useCallback((newTheme) => {
    setKidsThemeState((prev) => {
      const resolved = typeof newTheme === 'function' ? newTheme(prev) : newTheme;
      if (typeof window !== 'undefined') {
        localStorage.setItem('kids_theme', resolved);
      }
      return resolved;
    });
  }, []);

  // Context setTheme dynamically targets the active section
  const setTheme = useCallback((newTheme) => {
    if (isKids) {
      setKidsTheme(newTheme);
    } else {
      setMainTheme(newTheme);
    }
  }, [isKids, setKidsTheme, setMainTheme]);

  // Context toggleTheme dynamically toggles the active section
  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, [setTheme]);

  const contextValue = useMemo(() => ({
    theme: activeTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
    isKids,
    mainTheme,
    kidsTheme,
    setMainTheme,
    setKidsTheme,
  }), [activeTheme, setTheme, toggleTheme, resolvedTheme, isKids, mainTheme, kidsTheme, setMainTheme, setKidsTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
